import { useState, useEffect, useRef } from "react";

/**
 * ImageHoverPreview Component
 * Displays a fullscreen preview of an image when hovering over it
 * - If width > height: height fits viewport
 * - If height > width: width fits viewport
 */
const ImageHoverPreview = ({ enabled = true, children, imageSrc, imageAlt = "" }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0, aspectRatio: 1 });
  const imageRef = useRef(null);
  const timeoutRef = useRef(null);

  // Preload image to get dimensions
  useEffect(() => {
    if (!imageSrc || !enabled) return;

    const img = new Image();
    img.onload = () => {
      setImageSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
      });
    };
    img.src = imageSrc;
  }, [imageSrc, enabled]);

  const handleMouseEnter = (e) => {
    if (!enabled || !imageSrc) return;
    
    // Delay showing preview slightly to avoid accidental triggers
    timeoutRef.current = setTimeout(() => {
      setIsHovering(true);
      setMousePosition({ x: e.clientX, y: e.clientY });
    }, 100);
  };

  const handleMouseMove = (e) => {
    if (!enabled || !isHovering) return;
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovering(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate preview size and position based on image aspect ratio
  const getPreviewStyle = () => {
    if (!isHovering || !imageSrc) return { display: 'none' };

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 40; // Safe padding from edges

    let previewWidth, previewHeight;

    // Calculate max available space
    const maxWidth = viewportWidth - padding * 2;
    const maxHeight = viewportHeight - padding * 2;

    // If width > height (landscape): fit WIDTH to viewport
    if (imageSize.aspectRatio > 1) {
      // For landscape images, fit width to viewport
      previewWidth = maxWidth;
      previewHeight = previewWidth / imageSize.aspectRatio;
      
      // If calculated height exceeds viewport, adjust to fit height instead
      if (previewHeight > maxHeight) {
        previewHeight = maxHeight;
        previewWidth = previewHeight * imageSize.aspectRatio;
      }
    } 
    // If height > width (portrait): fit HEIGHT to viewport
    else {
      // For portrait images, fit height to viewport
      previewHeight = maxHeight;
      previewWidth = previewHeight * imageSize.aspectRatio;
      
      // If calculated width exceeds viewport, adjust to fit width instead
      if (previewWidth > maxWidth) {
        previewWidth = maxWidth;
        previewHeight = previewWidth / imageSize.aspectRatio;
      }
    }

    // Center the preview in viewport
    const left = (viewportWidth - previewWidth) / 2;
    const top = (viewportHeight - previewHeight) / 2;

    return {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      width: `${previewWidth}px`,
      height: `${previewHeight}px`,
      zIndex: 9999,
      pointerEvents: 'none',
      display: 'block',
    };
  };

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ 
          position: 'relative',
          width: '100%', 
          height: '100%',
          display: 'block'
        }}
      >
        {children}
      </div>

      {/* Fullscreen Preview Overlay */}
      {isHovering && imageSrc && enabled && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9998,
            pointerEvents: 'none',
          }}
        >
          <div style={getPreviewStyle()}>
            <img
              ref={imageRef}
              src={imageSrc}
              alt={imageAlt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                borderRadius: '8px',
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ImageHoverPreview;
